import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useApi } from '../hooks/useApi';
import { CompanyUsagePanel } from '../components/company/CompanyUsagePanel';

interface DashboardStats {
  ordersToday: number;
  ordersThisWeek: number;
  pendingPicks: number;
  lowStockItems: number;
  upcomingDeliveries: { date: string; count: number }[];
  routesToday?: number;
  routesInProgress?: number;
  routesCompleted?: number;
  deliveriesToday?: number;
  deliveriesCompleted?: number;
  deliveriesFailed?: number;
}

// Bento Dashboard Styles
const styles = `
  .bento-dashboard {
    display: grid;
    grid-template-columns: repeat(12, 1fr);
    gap: 20px;
    padding: 0;
  }

  .bento-card {
    background: white;
    border-radius: 16px;
    padding: 24px;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);
    transition: box-shadow 0.2s ease, transform 0.2s ease;
  }

  .bento-card:hover {
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  }

  .bento-card h3 {
    font-size: 13px;
    font-weight: 500;
    color: #637381;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    margin: 0 0 16px 0;
  }

  /* Stat Cards - Small */
  .stat-card {
    grid-column: span 3;
  }

  .stat-value {
    font-size: 42px;
    font-weight: 700;
    color: #1a1a1a;
    line-height: 1;
    margin-bottom: 8px;
  }

  .stat-label {
    font-size: 13px;
    color: #637381;
  }

  .stat-badge {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 4px 10px;
    border-radius: 20px;
    font-size: 12px;
    font-weight: 500;
    margin-top: 12px;
  }

  .stat-badge.attention {
    background: #FFF3CD;
    color: #856404;
  }

  .stat-badge.critical {
    background: #F8D7DA;
    color: #721C24;
  }

  .stat-badge.success {
    background: #D4EDDA;
    color: #155724;
  }

  /* Company usage */
  .gauge-card {
    grid-column: span 6;
    display: flex;
    flex-direction: column;
    align-items: stretch;
  }

  /* Upcoming Deliveries - Wide */
  .deliveries-card {
    grid-column: span 6;
  }

  .delivery-list {
    display: flex;
    flex-direction: column;
    gap: 12px;
  }

  .delivery-item {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 12px 16px;
    background: #f8f9fa;
    border-radius: 10px;
    cursor: pointer;
    transition: background 0.15s ease;
  }

  .delivery-item:hover {
    background: #f0f1f2;
  }

  .delivery-date {
    display: flex;
    align-items: center;
    gap: 12px;
  }

  .delivery-date-icon {
    width: 40px;
    height: 40px;
    background: linear-gradient(135deg, #4a9b8c 0%, #3d8577 100%);
    border-radius: 10px;
    display: flex;
    align-items: center;
    justify-content: center;
    color: white;
    font-weight: 600;
    font-size: 14px;
  }

  .delivery-date-text {
    display: flex;
    flex-direction: column;
  }

  .delivery-day {
    font-weight: 600;
    color: #1a1a1a;
    font-size: 14px;
  }

  .delivery-weekday {
    font-size: 12px;
    color: #637381;
  }

  .delivery-count {
    display: flex;
    align-items: center;
    gap: 8px;
  }

  .delivery-count-value {
    font-size: 20px;
    font-weight: 700;
    color: #1a1a1a;
  }

  .delivery-count-label {
    font-size: 12px;
    color: #637381;
  }

  .empty-state {
    text-align: center;
    padding: 32px;
    color: #637381;
  }

  .empty-state-icon {
    font-size: 32px;
    margin-bottom: 12px;
  }

  /* Quick Actions - Tall */
  .actions-card {
    grid-column: span 3;
    grid-row: span 2;
  }

  .action-list {
    display: flex;
    flex-direction: column;
    gap: 10px;
  }

  .action-item {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 14px 16px;
    background: #f8f9fa;
    border: none;
    border-radius: 10px;
    cursor: pointer;
    text-align: left;
    font-size: 14px;
    color: #1a1a1a;
    transition: all 0.15s ease;
    width: 100%;
  }

  .action-item:hover {
    background: #4a9b8c;
    color: white;
  }

  .action-item:hover svg {
    color: white;
  }

  .action-item svg {
    width: 20px;
    height: 20px;
    color: #4a9b8c;
    transition: color 0.15s ease;
  }

  /* Status Card */
  .status-card {
    grid-column: span 3;
  }

  .status-list {
    display: flex;
    flex-direction: column;
    gap: 12px;
  }

  .status-item {
    display: flex;
    align-items: center;
    gap: 12px;
  }

  .status-dot {
    width: 10px;
    height: 10px;
    border-radius: 50%;
    background: #10B981;
  }

  .status-dot.warning {
    background: #F59E0B;
  }

  .status-dot.error {
    background: #EF4444;
  }

  .status-label {
    font-size: 14px;
    color: #1a1a1a;
  }

  .status-badge {
    margin-left: auto;
    padding: 4px 10px;
    border-radius: 20px;
    font-size: 11px;
    font-weight: 500;
    background: #D4EDDA;
    color: #155724;
  }

  /* Routes Card */
  .routes-card {
    grid-column: span 6;
  }

  .routes-grid {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 16px;
  }

  .route-stat {
    text-align: center;
    padding: 20px;
    background: #f8f9fa;
    border-radius: 12px;
  }

  .route-stat-icon {
    width: 40px;
    height: 40px;
    margin: 0 auto 12px;
    border-radius: 10px;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .route-stat-icon.total {
    background: linear-gradient(135deg, #4a9b8c 0%, #3d8577 100%);
    color: white;
  }

  .route-stat-icon.active {
    background: linear-gradient(135deg, #F59E0B 0%, #D97706 100%);
    color: white;
  }

  .route-stat-icon.done {
    background: linear-gradient(135deg, #10B981 0%, #059669 100%);
    color: white;
  }

  .route-stat-value {
    font-size: 28px;
    font-weight: 700;
    color: #1a1a1a;
    margin-bottom: 4px;
  }

  .route-stat-label {
    font-size: 12px;
    color: #637381;
  }

  /* Header */
  .dashboard-header {
    margin-bottom: 24px;
  }

  .dashboard-title {
    font-size: 28px;
    font-weight: 700;
    color: #1a1a1a;
    margin: 0 0 4px 0;
  }

  .dashboard-subtitle {
    font-size: 14px;
    color: #637381;
    margin: 0;
  }

  /* Responsive */
  @media (max-width: 1200px) {
    .stat-card {
      grid-column: span 6;
    }
    .gauge-card {
      grid-column: span 6;
    }
    .deliveries-card {
      grid-column: span 12;
    }
    .actions-card {
      grid-column: span 6;
      grid-row: span 1;
    }
    .status-card {
      grid-column: span 6;
    }
    .routes-card {
      grid-column: span 12;
    }
  }

  @media (max-width: 768px) {
    .stat-card {
      grid-column: span 12;
    }
    .gauge-card {
      grid-column: span 12;
    }
    .actions-card {
      grid-column: span 12;
    }
    .status-card {
      grid-column: span 12;
    }
  }

  /* Loading skeleton */
  .skeleton {
    background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
    background-size: 200% 100%;
    animation: shimmer 1.5s infinite;
    border-radius: 8px;
  }

  @keyframes shimmer {
    0% { background-position: 200% 0; }
    100% { background-position: -200% 0; }
  }
`;

export default function Dashboard() {
  const navigate = useNavigate();
  const { fetchApi } = useApi();
  const [stats, setStats] = useState<DashboardStats>({
    ordersToday: 0,
    ordersThisWeek: 0,
    pendingPicks: 0,
    lowStockItems: 0,
    upcomingDeliveries: [],
    routesToday: 0,
    routesInProgress: 0,
    routesCompleted: 0,
    deliveriesToday: 0,
    deliveriesCompleted: 0,
    deliveriesFailed: 0,
  });

  useEffect(() => {
    async function loadData() {
      try {
        const statsData = await fetchApi<DashboardStats>('/dashboard/stats');

        if (statsData) {
          setStats({
            ordersToday: statsData.ordersToday ?? 0,
            ordersThisWeek: statsData.ordersThisWeek ?? 0,
            pendingPicks: statsData.pendingPicks ?? 0,
            lowStockItems: statsData.lowStockItems ?? 0,
            upcomingDeliveries: statsData.upcomingDeliveries ?? [],
            routesToday: statsData.routesToday ?? 0,
            routesInProgress: statsData.routesInProgress ?? 0,
            routesCompleted: statsData.routesCompleted ?? 0,
            deliveriesToday: statsData.deliveriesToday ?? 0,
            deliveriesCompleted: statsData.deliveriesCompleted ?? 0,
            deliveriesFailed: statsData.deliveriesFailed ?? 0,
          });
        }

      } catch (err) {
        console.error('Failed to load dashboard:', err);
      }
    }
    loadData();
  }, [fetchApi]);

  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr);
    return {
      day: date.getDate(),
      weekday: date.toLocaleDateString('en-GB', { weekday: 'short' }),
      month: date.toLocaleDateString('en-GB', { month: 'short' }),
    };
  };

  return (
    <>
      <style>{styles}</style>

      <div className="dashboard-header">
        <h1 className="dashboard-title">Dashboard</h1>
        <p className="dashboard-subtitle">Welcome back! Here's what's happening today.</p>
      </div>

      <div className="bento-dashboard">
        {/* Orders Today */}
        <div className="bento-card stat-card">
          <h3>Orders Today</h3>
          <div className="stat-value">{stats.ordersToday}</div>
          <div className="stat-label">With delivery dates</div>
        </div>

        {/* Orders This Week */}
        <div className="bento-card stat-card">
          <h3>This Week</h3>
          <div className="stat-value">{stats.ordersThisWeek}</div>
          <div className="stat-label">Orders to fulfil</div>
        </div>

        {/* Pending Picks */}
        <div className="bento-card stat-card">
          <h3>Pending Picks</h3>
          <div className="stat-value">{stats.pendingPicks}</div>
          <div className="stat-label">Pick lists waiting</div>
          {stats.pendingPicks > 0 && (
            <div className="stat-badge attention">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="12" cy="12" r="10" />
                <line x1="12" y1="8" x2="12" y2="12" />
                <line x1="12" y1="16" x2="12.01" y2="16" />
              </svg>
              Action needed
            </div>
          )}
        </div>

        {/* Low Stock */}
        <div className="bento-card stat-card">
          <h3>Low Stock</h3>
          <div className="stat-value">{stats.lowStockItems}</div>
          <div className="stat-label">Items below threshold</div>
          {stats.lowStockItems > 0 && (
            <div className="stat-badge critical">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" />
                <line x1="12" y1="9" x2="12" y2="13" />
                <line x1="12" y1="17" x2="12.01" y2="17" />
              </svg>
              Reorder
            </div>
          )}
        </div>

        {/* Routes Today */}
        <div className="bento-card routes-card">
          <h3>Today's Routes</h3>
          <div className="routes-grid">
            <div className="route-stat">
              <div className="route-stat-icon total">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <rect x="1" y="3" width="15" height="13" rx="1" />
                  <path d="M16 8h4l3 3v5h-7V8z" />
                  <circle cx="5.5" cy="18.5" r="2.5" />
                  <circle cx="18.5" cy="18.5" r="2.5" />
                </svg>
              </div>
              <div className="route-stat-value">{stats.routesToday || 0}</div>
              <div className="route-stat-label">Total Routes</div>
            </div>
            <div className="route-stat">
              <div className="route-stat-icon active">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <circle cx="12" cy="12" r="10" />
                  <polyline points="12,6 12,12 16,14" />
                </svg>
              </div>
              <div className="route-stat-value">{stats.routesInProgress || 0}</div>
              <div className="route-stat-label">In Progress</div>
            </div>
            <div className="route-stat">
              <div className="route-stat-icon done">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M22 11.08V12a10 10 0 11-5.93-9.14" />
                  <polyline points="22,4 12,14.01 9,11.01" />
                </svg>
              </div>
              <div className="route-stat-value">{stats.routesCompleted || 0}</div>
              <div className="route-stat-label">Completed</div>
            </div>
          </div>
        </div>

        <CompanyUsagePanel compact />

        {/* Upcoming Deliveries */}
        <div className="bento-card deliveries-card">
          <h3>Upcoming Deliveries</h3>
          {stats.upcomingDeliveries.length > 0 ? (
            <div className="delivery-list">
              {stats.upcomingDeliveries.slice(0, 5).map((delivery, i) => {
                const { day, weekday, month } = formatDate(delivery.date);
                return (
                  <div
                    key={i}
                    className="delivery-item"
                    onClick={() => navigate(`/routes?date=${delivery.date}`)}
                  >
                    <div className="delivery-date">
                      <div className="delivery-date-icon">{day}</div>
                      <div className="delivery-date-text">
                        <span className="delivery-day">{weekday}, {month} {day}</span>
                        <span className="delivery-weekday">Scheduled delivery</span>
                      </div>
                    </div>
                    <div className="delivery-count">
                      <span className="delivery-count-value">{delivery.count}</span>
                      <span className="delivery-count-label">orders</span>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="empty-state">
              <div className="empty-state-icon">📦</div>
              <p>No upcoming deliveries scheduled</p>
            </div>
          )}
        </div>

        {/* Quick Actions */}
        <div className="bento-card actions-card">
          <h3>Quick Actions</h3>
          <div className="action-list">
            <button className="action-item" onClick={() => navigate('/pick-lists')}>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2" />
                <rect x="9" y="3" width="6" height="4" rx="1" />
                <path d="M9 13h6m-3-3v6" />
              </svg>
              Create Pick List
            </button>
            <button className="action-item" onClick={() => navigate('/orders')}>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z" />
                <polyline points="14,2 14,8 20,8" />
                <line x1="16" y1="13" x2="8" y2="13" />
                <line x1="16" y1="17" x2="8" y2="17" />
              </svg>
              View Today's Orders
            </button>
            <button className="action-item" onClick={() => navigate('/routes/build')}>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <rect x="1" y="3" width="15" height="13" rx="1" />
                <path d="M16 8h4l3 3v5h-7V8z" />
                <circle cx="5.5" cy="18.5" r="2.5" />
                <circle cx="18.5" cy="18.5" r="2.5" />
              </svg>
              Build Route
            </button>
            <button className="action-item" onClick={() => navigate('/stock-takes')}>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M21 16V8a2 2 0 00-1-1.73l-7-4a2 2 0 00-2 0l-7 4A2 2 0 003 8v8a2 2 0 001 1.73l7 4a2 2 0 002 0l7-4A2 2 0 0021 16z" />
                <polyline points="3.27,6.96 12,12.01 20.73,6.96" />
                <line x1="12" y1="22.08" x2="12" y2="12" />
              </svg>
              Start Stock Take
            </button>
            <button className="action-item" onClick={() => navigate('/returns')}>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <polyline points="1,4 1,10 7,10" />
                <path d="M3.51 15a9 9 0 102.13-9.36L1 10" />
              </svg>
              Process Returns
            </button>
          </div>
        </div>

        {/* System Status */}
        <div className="bento-card status-card">
          <h3>System Status</h3>
          <div className="status-list">
            <div className="status-item">
              <span className="status-dot" />
              <span className="status-label">Shopify Sync</span>
              <span className="status-badge">Connected</span>
            </div>
            <div className="status-item">
              <span className="status-dot" />
              <span className="status-label">Date Picker</span>
              <span className="status-badge">Active</span>
            </div>
            <div className="status-item">
              <span className="status-dot" />
              <span className="status-label">Warehouse</span>
              <span className="status-badge">Ready</span>
            </div>
            <div className="status-item">
              <span className="status-dot" />
              <span className="status-label">Route Engine</span>
              <span className="status-badge">Ready</span>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
